import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Fragment,
} from 'react';
import Axios, { CancelTokenSource } from 'axios';
import _, { isNumber } from 'lodash';
import { Select, Progress, Dimmer } from 'semantic-ui-react';
import { ATMButton, ATMLoader, ATMModal, ATMTable } from 'shared-it-appmod-ui';
import { FileCategory } from 'src/constants';
import { useFileContext } from 'src/contexts/file.context';
import Lang from 'src/libraries/language';
import { IFile } from 'src/models/file.model';
import { getFileStatus } from 'src/selectors/file.selector';
import { actionTypes } from 'src/ducks/file.duck';
import {
  ToastError,
  ToastSuccess,
} from 'src/components/atoms/toaster/toaster.component';
import FileDownloader from 'src/components/atoms/file-downloader/file-downloader.component';
import Confirm from 'src/components/atoms/confirm/confirm.component';
import styles from './file-manager.module.scss';

type IProps = {
  name?: string;
  files?: IFile[];
  title?: string;
  allowedTypes?: FileCategory[];
  limit?: number;
  inline?: boolean;
  editable?: boolean;
  loading?: boolean;
  error?: boolean;
  fileRestriction?: string[];
  trigger?: (open: () => void) => React.ReactNode;
  onSuccess?: (file: IFile) => void;
  onError?: (error: any) => void;
  onClose?: (files: IFile[]) => void;
  onUpdate?: (files: IFile[]) => void;
  onDelete?: (file: IFile) => void;
};

export const getRandomId = () => {
  const min = 100000;
  return Math.round(Math.random() * (999999 - min) + min);
};

export const getFileType = (filename: string): string => {
  const type = filename.split('.');

  return type[type.length - 1].toLowerCase();
};

const FileManager: React.FC<IProps> = ({
  name,
  files = [],
  limit = 20,
  allowedTypes = [],
  inline = false,
  title = Lang.TTL_FILE_MANAGER,
  editable = true,
  loading = false,
  error,
  fileRestriction,
  trigger,
  onClose,
  onError,
  onSuccess,
  onUpdate,
  onDelete,
}) => {
  const { state, actions } = useFileContext();

  const [isOpen, setIsOpen] = useState(false);
  const [isConfirm, setIsConfirm] = useState<Partial<IFile>>();
  const [uploading, setUploading] = useState<
    Record<
      number,
      {
        total: number;
        cancelToken: CancelTokenSource;
      }
    >
  >({});
  const [list, setList] = useState<Partial<IFile>[]>(files);
  const inputRefs = useRef<HTMLInputElement | null[]>([]);

  useEffect(() => {
    if (onUpdate) {
      onUpdate(list as IFile[]);
    }
  }, [list, onUpdate]);

  useEffect(() => {
    // This will prevent infinite loop
    if (!onUpdate && editable) {
      // This logic will handle the update of the files props.
      // If the files have the latest data, will update the list accordingly
      if (files.length) {
        setList((values) => {
          if (_.isEqual(values.sort(), files.sort())) {
            let items: Partial<IFile>[] = [];

            values.map((file) => {
              const data = files.find((v) => v.fileName === file.fileName);

              if (!file.uploadId && !data) {
                items = [...items, file];
              } else if (data) {
                items = [...items, data];
              }
            });

            return items;
          }

          return files;
        });
      } else {
        setList([]);
      }
    }
  }, [files, editable, setList, onUpdate]);

  const handleAdd = useCallback(() => {
    setList((values) => [
      ...values,
      {
        id: getRandomId(),
        uploadTypeId: allowedTypes.length === 1 ? allowedTypes[0] : undefined,
      },
    ]);
  }, [allowedTypes, setList, getRandomId]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose(list.filter((value) => value.fileName) as IFile[]);
    }

    setList([]);
    setIsOpen(false);
  }, [onClose, list, setList, setIsOpen]);

  const handleRemove = useCallback(
    (id) => {
      setList((values) => values.filter((value) => value.id !== id));

      setUploading((values) =>
        Object.keys(values).reduce((items, key) => {
          const itemList = {
            ...items,
          };

          if (Number(key) !== id) {
            itemList[key] = values[key];
          }

          return itemList;
        }, {})
      );
    },
    [setList, setUploading]
  );

  const handleDelete = useCallback(
    async (file) => {
      const detail = list.find((value) => value.fileName === file.fileName);
      if (detail) {
        await actions.uploadDELETE(file.fileName);

        if (onDelete) {
          onDelete(file);
        }

        ToastSuccess(
          Lang.formatString(Lang.MSG_FILE_DELETE_SUCCESS, file.docName)
        );

        handleRemove(detail.id);
      }

      setIsConfirm(undefined);
    },
    [actions, list, handleRemove, setIsConfirm, onDelete]
  );

  const handleList = useCallback(
    (id, data: Partial<IFile>) => {
      setList((values) => {
        return values.reduce((items: Partial<IFile>[], item) => {
          let listItems = [...items];

          if (item.id === id) {
            listItems = [
              ...listItems,
              {
                ...item,
                ...data,
              },
            ];
          } else {
            listItems = [...listItems, item];
          }

          return listItems;
        }, []);
      });
    },
    [setList]
  );

  const handleUpload = useCallback(
    async (id, event) => {
      const docName = event.currentTarget.files[0].name;

      handleList(id, {
        docName,
      });

      const cancelToken = Axios.CancelToken.source();

      setUploading((values) => ({
        ...values,
        [id]: {
          total: 0,
          cancelToken,
        },
      }));

      if (fileRestriction && !fileRestriction.includes(getFileType(docName))) {
        ToastError(
          `${Lang.NTE_FILE_TYPE_RESTRICION} ${fileRestriction.toString()}`
        );

        handleRemove(id);
        return;
      }
      const result = await actions.uploadPOST(event.currentTarget.files[0], {
        cancelToken: cancelToken.token,
        onUploadProgress: (progress) => {
          const total = Math.round((100 * progress.loaded) / progress.total);

          setUploading((values) => ({
            ...values,
            [id]: {
              ...values[id],
              total,
            },
          }));
        },
      });
      if (result.payload) {
        setUploading((values) =>
          Object.keys(values).reduce((items, key) => {
            const itemList = {
              ...items,
            };

            if (Number(key) !== id) {
              itemList[key] = values[key];
            }

            return itemList;
          }, {})
        );

        if (onSuccess) {
          const temp = list.find((item) => item.id === id);

          onSuccess({
            ...temp,
            ...result.payload,
            docName,
          });
        }

        handleList(id, {
          ...result.payload,
        });
      } else if (result.error) {
        ToastError(result.error?.options?.message);

        handleRemove(id);
      }
    },
    [actions, list, handleList, handleRemove, setUploading, onSuccess, onError]
  );

  const statusDelete = getFileStatus(state, actionTypes.FILE_DELETE);

  const content = (
    <>
      {loading && (
        <Dimmer active inverted>
          <ATMLoader inverted />
        </Dimmer>
      )}
      <ATMTable name={name} color={error ? 'red' : undefined}>
        <ATMTable.Header>
          <ATMTable.Row>
            <ATMTable.HeaderCell width={5}>
              {Lang.LBL_FILE_TYPE}
            </ATMTable.HeaderCell>
            <ATMTable.HeaderCell width={9}>{Lang.LBL_FILE}</ATMTable.HeaderCell>
            <ATMTable.HeaderCell width={2}>
              {Lang.LBL_ACTIONS}
            </ATMTable.HeaderCell>
          </ATMTable.Row>
        </ATMTable.Header>

        <ATMTable.Body>
          {list.map((item, i) => (
            <Fragment key={`row_${item.id}_${i}`}>
              {item.fileName ? (
                <ATMTable.Row>
                  <ATMTable.Cell>
                    {item.uploadTypeId !== undefined
                      ? Lang.FILE_CATEGORY[item.uploadTypeId ?? '']
                      : ''}
                  </ATMTable.Cell>
                  <ATMTable.Cell>{item.docName}</ATMTable.Cell>
                  <ATMTable.Cell textAlign="right">
                    <div className={styles.actions}>
                      {editable && (
                        <ATMButton
                          icon="trash alternate outline"
                          type="button"
                          onClick={() => setIsConfirm(item)}
                        />
                      )}

                      <FileDownloader file={item} />
                    </div>
                  </ATMTable.Cell>
                </ATMTable.Row>
              ) : (
                <ATMTable.Row>
                  <ATMTable.Cell>
                    <Select
                      key={`type_${list.length}_${i}`}
                      placeholder={Lang.LBL_SELECT}
                      defaultValue={item?.uploadTypeId ?? undefined}
                      disabled={allowedTypes.length === 1}
                      options={Object.entries(FileCategory)
                        .filter(
                          ([, value]) =>
                            isNumber(value) &&
                            (!allowedTypes.length ||
                              (allowedTypes.length &&
                                allowedTypes.includes(value)))
                        )
                        .map(([, value]) => ({
                          key: value,
                          value,
                          text: Lang.FILE_CATEGORY[value],
                        }))}
                      onChange={(e, { value }) =>
                        handleList(item.id, {
                          uploadTypeId: value as number,
                        })
                      }
                      selectOnBlur={false}
                    />
                  </ATMTable.Cell>
                  <ATMTable.Cell>
                    {item.docName ? (
                      item.docName
                    ) : (
                      <ATMButton
                        secondary
                        type="button"
                        disabled={
                          list[i].uploadTypeId === undefined &&
                          allowedTypes.length !== 1
                        }
                        onClick={() => {
                          if (inputRefs.current[i]) {
                            inputRefs.current[i].click();
                          }
                        }}
                      >
                        {Lang.LBL_BROWSE}
                      </ATMButton>
                    )}

                    <input
                      // eslint-disable-next-line no-return-assign
                      ref={(el) => (inputRefs.current[i] = el)}
                      type="file"
                      onChange={(event) => handleUpload(item.id, event)}
                      className="hidden"
                    />
                  </ATMTable.Cell>
                  <ATMTable.Cell textAlign="right">
                    <div className={styles.actions}>
                      {item.id && uploading[item.id] !== undefined ? (
                        <>
                          <Progress
                            className={styles.progress}
                            percent={uploading[item.id].total}
                            size="small"
                            progress
                            indicating
                          />
                          <ATMButton
                            icon="stop circle outline"
                            type="button"
                            onClick={() => {
                              if (item.id) {
                                uploading[item.id].cancelToken.cancel(
                                  Lang.MSG_UPLOAD_CANCEL_SUCCESS
                                );
                              }

                              handleRemove(item.id);
                            }}
                          />
                        </>
                      ) : (
                        <ATMButton
                          icon="trash alternate outline"
                          type="button"
                          onClick={() => handleRemove(item.id)}
                        />
                      )}
                    </div>
                  </ATMTable.Cell>
                </ATMTable.Row>
              )}
            </Fragment>
          ))}
        </ATMTable.Body>
        {editable && (
          <ATMTable.Footer>
            <ATMTable.Row>
              <ATMTable.HeaderCell colSpan={4}>
                <ATMButton
                  secondary
                  type="button"
                  icon="plus"
                  disabled={limit !== undefined && limit === list.length}
                  onClick={handleAdd}
                  content={Lang.LBL_FILE_ADD}
                />
              </ATMTable.HeaderCell>
            </ATMTable.Row>
          </ATMTable.Footer>
        )}
      </ATMTable>

      <Confirm
        open={!!isConfirm}
        size="tiny"
        onCancel={() => setIsConfirm(undefined)}
        header={Lang.TTL_CONFIRM_DELETE}
        content={Lang.MSG_FILE_CONFIRM_DELETE}
        loading={statusDelete.fetching}
        onConfirm={() => handleDelete(isConfirm)}
        confirmText={Lang.LBL_CONFIRM_DELETE}
      />
    </>
  );

  if (inline) {
    return content;
  }

  return (
    <ATMModal
      open={isOpen}
      size="large"
      onClose={handleClose}
      closeOnDimmerClick={false}
      trigger={
        trigger ? (
          trigger(() => setIsOpen(true))
        ) : (
          <ATMButton
            secondary
            type="button"
            content={Lang.LBL_FILE_ADD}
            icon="plus"
            onClick={() => setIsOpen(true)}
          />
        )
      }
    >
      <ATMModal.Header>{title.replace('?', '')}</ATMModal.Header>
      <ATMModal.Content scrolling>{content}</ATMModal.Content>
      <ATMModal.Actions>
        <ATMButton
          secondary
          type="button"
          onClick={handleClose}
          disabled={!!Object.keys(uploading).length}
        >
          {Lang.LBL_CLOSE}
        </ATMButton>
      </ATMModal.Actions>
    </ATMModal>
  );
};

export default FileManager;
